import React, { useState, useRef, useEffect } from 'react';
import './ImageVideoSlider.css';

// Define separate types for different media types
interface BaseMediaItem {
  type: 'image' | 'video' | 'youtube';
  thumbnail: string;
}

interface ImageVideoMediaItem extends BaseMediaItem {
  type: 'image' | 'video';
  url: string;
}

interface YoutubeMediaItem extends BaseMediaItem {
  type: 'youtube';
  youtubeId: string;
}

type MediaItem = ImageVideoMediaItem | YoutubeMediaItem;

const ImageVideoSlider: React.FC<{
  media: MediaItem[];
  title: string;
  onClose: () => void;
  isOpening: boolean;
}> = ({ media, title, onClose, isOpening }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right'>('left');
  const [isClosing, setIsClosing] = useState(false);
  const thumbnailsRef = useRef<HTMLDivElement>(null);

  // Preload all media items
  useEffect(() => {
    media.forEach(item => {
      if (item.type === 'image') {
        const img = new Image();
        img.src = (item as ImageVideoMediaItem).url;
      }
    });
  }, [media]);

  const changeSlide = (direction: 'left' | 'right') => {
    if (isTransitioning) return;

    setIsTransitioning(true);
    setSlideDirection(direction);

    const nextIndex = direction === 'right' 
      ? (currentIndex + 1) % media.length
      : (currentIndex - 1 + media.length) % media.length;

    setTimeout(() => {
      setCurrentIndex(nextIndex);
      setIsTransitioning(false);
    }, 500); // Match transition duration
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(onClose, 300);
  };

  const renderMediaContent = (item: MediaItem) => {
    switch (item.type) {
      case 'youtube':
        return (
          <iframe
            src={`https://www.youtube.com/embed/${item.youtubeId}?autoplay=0`}
            allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title={`${title} - YouTube video`}
          />
        );
      case 'video':
        return <video src={item.url} controls />;
      default:
        return <img src={item.url} alt={title} />;
    }
  };

  const scrollThumbnails = (direction: 'left' | 'right') => {
    if (thumbnailsRef.current) {
      const scrollAmount = 200; // Adjust this value as needed
      thumbnailsRef.current.scrollLeft += direction === 'left' ? -scrollAmount : scrollAmount;
    }
  };

  return (
    <div className={`slider-overlay ${isClosing ? 'closing' : ''} ${isOpening ? 'opening' : ''}`}>
      <div className="slider-header">
        <h2>{title}</h2>
        <button className="close-button" onClick={handleClose}>×</button>
      </div>
      
      <div className="slider-main">
        <button className="nav-button prev" onClick={() => changeSlide('left')}>‹</button>
        
        <div className="slider-content">
          {media.map((item, index) => {
            const isTargetSlide = slideDirection === 'right' ? 
              index === (currentIndex + 1) % media.length :
              index === (currentIndex - 1 + media.length) % media.length;
            
            if (index !== currentIndex && !isTargetSlide) return null;
            
            let slideClass = '';
            if (index === currentIndex) {
              slideClass = isTransitioning ? 'outgoing' : 'current';
            } else if (isTargetSlide) {
              slideClass = isTransitioning ? 'incoming' : (
                slideDirection === 'right' ? 'after' : 'before'
              );
            }
            
            return (
              <div 
                key={index}
                className={`media-container ${slideClass} ${
                  isTransitioning ? `sliding-${slideDirection}` : ''
                }`}
              >
                {renderMediaContent(item)}
              </div>
            );
          })}
        </div>

        <button className="nav-button next" onClick={() => changeSlide('right')}>›</button>
      </div>

      <div className="thumbnails-container">
        <button className="thumb-nav prev" onClick={() => scrollThumbnails('left')}>‹</button>
        <div className="thumbnails-scroll" ref={thumbnailsRef}>
          {media.map((item, index) => (
            <div
              key={index}
              className={`thumbnail ${index === currentIndex ? 'active' : ''}`}
              onClick={() => setCurrentIndex(index)}
            >
              <img src={item.thumbnail} alt={`${title} ${index + 1}`} />
              {(item.type === 'video' || item.type === 'youtube') && (
                <div className="video-icon" />
              )}
            </div>
          ))}
        </div>
        <button className="thumb-nav next" onClick={() => scrollThumbnails('right')}>›</button>
      </div>
    </div>
  );
};

export default ImageVideoSlider; 